import { Controller } from "@hotwired/stimulus"

// The header's Pulse popup (#3290), alongside the dropdown controller that
// owns open and close: the body loads into the frame on hover or focus of
// the button, or on the first open, and every open is the read receipt (the
// dot goes, the account is stamped). A prefetch registers no receipt; the
// open that follows waits for the body if it is still in flight (#3538).
export default class extends Controller {
  static targets = ["button", "frame"]
  static values = { url: String }

  connect() {
    this.placeholder = this.frameTarget.innerHTML
    // Turbo snapshots the page when the next visit starts: a loaded frame
    // and an open disclosure would come back stale on a history restore
    this.beforeCache = () => this.#reset()
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
  }

  prefetch() {
    if (!this.frameTarget.src) this.frameTarget.src = this.urlValue
  }

  open() {
    if (this.buttonTarget.getAttribute("aria-expanded") !== "true") return

    this.buttonTarget.classList.remove("app-header__pulse--unread")
    this.buttonTarget.setAttribute("aria-label", "Pulse")
    if (!this.frameTarget.src) {
      // The receipt waits for the body: stamping first can mark the
      // changelog seen before the entry it would have shown as unread renders
      this.#stampOnLoad()
      this.frameTarget.src = this.urlValue
    } else if (!this.frameTarget.querySelector("[data-week-start]")) {
      // src is set the moment a prefetch or an earlier open starts the
      // request: no card means the load is still in flight, and the receipt
      // waits for it, once; stamping now would fall back to the unseen
      // latest week
      this.#stampOnLoad()
    } else if (this.#stale()) {
      // A document alive across the Monday rollover would re-show last
      // week's card; refresh, then stamp what actually loaded (#3329)
      this.#stampOnLoad()
      this.frameTarget.reload()
    } else {
      this.#stamp()
    }
  }

  #stampOnLoad() {
    if (this.awaitingBody) return

    this.awaitingBody = true
    this.frameTarget.addEventListener("turbo:frame-load", () => {
      this.awaitingBody = false
      this.#stamp()
    }, { once: true })
  }

  #stale() {
    const staleAfter = this.frameTarget.querySelector("[data-stale-after]")?.dataset.staleAfter
    if (!staleAfter) return false

    return new Date().toISOString().slice(0, 10) >= staleAfter
  }

  #reset() {
    document.dispatchEvent(new CustomEvent("dropdown:close"))
    this.frameTarget.removeAttribute("src")
    this.frameTarget.innerHTML = this.placeholder
    this.awaitingBody = false
  }

  #stamp() {
    const headers = { Accept: "application/json" }
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) headers["X-CSRF-Token"] = token
    // The receipt names the week the loaded card shows, so a PATCH landing
    // after a Monday rollover cannot stamp a week that was never shown (#3329)
    const week = this.frameTarget.querySelector("[data-week-start]")?.dataset.weekStart
    const url = week ? `${this.urlValue}?week_start=${week}` : this.urlValue
    fetch(url, { method: "PATCH", headers })
  }
}
